/* resync */
import React, { useEffect, useMemo, useState } from 'react'
import { CButton, CFormInput, CFormLabel, CFormSelect, CFormTextarea, CNav, CNavItem, CNavLink } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { icon } from '../../../shared/icons'
import { useBostonState, emit } from '../lib/useBoston'
import { Storage } from '../lib/store'
import { useUI } from '../../../shared/ui/UIProvider'
import { biaListPrompts, biaSavePrompt, biaUpdatePrompt } from '../lib/biaApi'

/* Editor de system prompts oficiales.
   Guardar persiste en public-api-boston /admin/ai. localStorage solo se usa
   para borradores temporales y preferencias visuales. */

const PROMPT_USER = 'admin_v2'
const DRAFT_PREFIX = 'boston_ai_prompt_draft_'

const AGENTS = [
  { id: 'agent1_sst', label: 'Agente 1 - .sst a IR', desc: 'Interpreta scripts Sabre Scribe en texto plano (.sst) y devuelve IR JSON.' },
  { id: 'agent2_ssc', label: 'Agente 2 - .ssc a IR', desc: 'Decompila scripts Scribe binarios a partir de strings extraidas, devuelve IR JSON.' },
  { id: 'agent3_gen', label: 'Agente 3 - IR/NL a data.json', desc: 'Genera el Custom Script data.json desde un IR o desde lenguaje natural.' },
  {
    id: 'agent4_scriptlogic',
    label: 'Agente 4 - IR+data.json a app.js',
    desc: 'Genera el handler JS con logica dinamica. No toca el core del custom script.',
  },
]

function draftKey(agent) {
  return DRAFT_PREFIX + agent
}

function emptyForm(agent, nextVersion = 1) {
  return {
    id: '',
    agent,
    version: nextVersion,
    content: '',
    comment: '',
    isActive: false,
    createdUser: PROMPT_USER,
    updatedUser: PROMPT_USER,
  }
}

function promptForm(row, agent) {
  if (!row) return emptyForm(agent)
  return {
    id: row.id || '',
    agent: row.agent || agent,
    version: Number(row.version || 1),
    content: row.content || '',
    comment: row.comment || '',
    isActive: Boolean(row.isActive),
    createdUser: row.createdUser || PROMPT_USER,
    updatedUser: PROMPT_USER,
  }
}

function nextVersion(rows) {
  const versions = rows.map((item) => Number(item.version || 0)).filter((value) => Number.isFinite(value))
  return Math.max(0, ...versions) + 1
}

function sortPrompts(rows) {
  return [...rows].sort((a, b) => Number(b.isActive) - Number(a.isActive) || Number(b.version || 0) - Number(a.version || 0) || Number(b.id || 0) - Number(a.id || 0))
}

function readDraft(agent) {
  try {
    const raw = localStorage.getItem(draftKey(agent))
    return raw ? JSON.parse(raw) : null
  } catch (_) {
    return null
  }
}

function writeDraft(agent, form) {
  localStorage.setItem(
    draftKey(agent),
    JSON.stringify({
      agent,
      version: Number(form.version || 1),
      content: form.content || '',
      comment: form.comment || '',
      isActive: Boolean(form.isActive),
      savedAt: new Date().toISOString(),
    }),
  )
}

function clearDraft(agent) {
  try {
    localStorage.removeItem(draftKey(agent))
  } catch (_) {
    /* sin storage */
  }
}

function AiPromptView() {
  const state = useBostonState()
  const { toast, confirm } = useUI()

  if (!state.ai.promptTab) state.ai.promptTab = 'agent1_sst'
  const tab = state.ai.promptTab
  const current = AGENTS.find((x) => x.id === tab) || AGENTS[0]

  const [prompts, setPrompts] = useState([])
  const [selectedId, setSelectedId] = useState('new')
  const [form, setForm] = useState(emptyForm(tab))
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [draft, setDraft] = useState(null)

  const selectedPrompt = useMemo(() => prompts.find((item) => String(item.id) === String(selectedId)) || null, [prompts, selectedId])

  async function loadPrompts(agent = tab, keepId = selectedId) {
    setLoading(true)
    try {
      const rows = sortPrompts(await biaListPrompts(agent))
      setPrompts(rows)
      const preferred =
        rows.find((item) => String(item.id) === String(keepId)) ||
        rows.find((item) => item.isActive) ||
        rows[0] ||
        null
      if (preferred) {
        setSelectedId(String(preferred.id))
        setForm(promptForm(preferred, agent))
      } else {
        setSelectedId('new')
        setForm(emptyForm(agent))
      }
      setDraft(readDraft(agent))
    } catch (error) {
      toast(error.message || 'No se pudieron cargar prompts', 'danger')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadPrompts(tab, selectedId)
  }, [tab])

  function pickTab(id) {
    state.ai.promptTab = id
    emit()
  }

  function setField(key, value) {
    setForm((currentForm) => ({ ...currentForm, [key]: value }))
  }

  function selectPrompt(id) {
    setSelectedId(id)
    if (id === 'new') {
      setForm(emptyForm(tab, nextVersion(prompts)))
      return
    }
    const row = prompts.find((item) => String(item.id) === String(id))
    setForm(promptForm(row, tab))
  }

  async function saveBackend() {
    if (!String(form.content || '').trim()) {
      toast('Ingresa el contenido del prompt', 'warning')
      return
    }

    const payload = {
      agent: tab,
      version: Number(form.version || 1),
      content: form.content,
      comment: form.comment || null,
      isActive: Boolean(form.isActive),
      createdUser: form.createdUser || PROMPT_USER,
      updatedUser: PROMPT_USER,
    }

    setSaving(true)
    try {
      const saved = form.id ? await biaUpdatePrompt(form.id, payload) : await biaSavePrompt(payload)
      const nextId = saved?.id || form.id
      clearDraft(tab)
      setDraft(null)
      await loadPrompts(tab, nextId ? String(nextId) : 'new')
      toast(form.id ? 'Prompt actualizado en backend' : 'Prompt creado en backend', 'success')
    } catch (error) {
      toast(error.message || 'No se pudo guardar el prompt', 'danger')
    } finally {
      setSaving(false)
    }
  }

  function saveLocalDraft() {
    try {
      writeDraft(tab, form)
      setDraft(readDraft(tab))
      toast('Borrador local guardado', 'success')
    } catch (error) {
      toast('No se pudo guardar el borrador local', 'danger')
    }
  }

  async function loadLocalDraft() {
    const nextDraft = readDraft(tab)
    if (!nextDraft) {
      toast('No hay borrador local para este agente', 'info')
      return
    }
    const ok = await confirm({
      title: 'Cargar borrador local',
      body: 'Esto reemplaza el contenido visible, pero no guarda en backend hasta presionar Guardar.',
      confirmText: 'Cargar borrador',
      cancelText: 'Cancelar',
    })
    if (!ok) return
    setSelectedId('new')
    setForm({
      ...emptyForm(tab, nextDraft.version || nextVersion(prompts)),
      ...nextDraft,
      id: '',
      agent: tab,
      createdUser: PROMPT_USER,
      updatedUser: PROMPT_USER,
    })
  }

  async function discardLocalDraft() {
    const ok = await confirm({
      title: 'Descartar borrador local',
      body: 'Se eliminara solo el borrador temporal de este agente.',
      confirmText: 'Descartar',
      cancelText: 'Cancelar',
    })
    if (!ok) return
    clearDraft(tab)
    setDraft(null)
    toast('Borrador local descartado', 'success')
  }

  async function resetFromBackend() {
    const ok = await confirm({
      title: 'Recargar desde backend',
      body: 'Se descartaran cambios no guardados en pantalla y se recargara el prompt seleccionado desde /admin/ai.',
      confirmText: 'Recargar',
      cancelText: 'Cancelar',
    })
    if (!ok) return
    if (selectedPrompt) setForm(promptForm(selectedPrompt, tab))
    else await loadPrompts(tab, selectedId)
  }

  function onModelChange(value) {
    state.ai.model = value
    try {
      localStorage.removeItem('boston_ai_model_v1')
      if (value) localStorage.setItem('boston_ai_model_override_v2', value)
      else localStorage.removeItem('boston_ai_model_override_v2')
    } catch (e) {
      /* sin persistencia */
    }
    state.config.aiModel = value
    Storage.saveConfig(state.config)
    emit()
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Prompts de los Agentes IA</h1>
          <p>
            Los prompts oficiales se guardan en backend mediante <code>/admin/ai</code>. El navegador solo conserva borradores locales temporales.
          </p>
        </div>
      </div>

      <div className="card-x">
        <h3>Modelo Claude</h3>
        <p className="text-muted" style={{ fontSize: 13 }}>
          La API key de Anthropic se administra de forma segura en backend desde Configuracion, via /admin/ai/settings.
        </p>
        <div>
          <CFormLabel>Modelo (aplica a los agentes)</CFormLabel>
          <CFormSelect value={state.ai.model} onChange={(event) => onModelChange(event.target.value)}>
            <option value="">Usar configuración DB</option>
            <option value="claude-opus-5">Claude Opus 5 (override)</option>
            <option value="claude-sonnet-4-5">Claude Sonnet 4.5 (recomendado)</option>
            <option value="claude-haiku-4-5-20251001">Claude Haiku 4.5 (rapido y barato)</option>
          </CFormSelect>
        </div>
      </div>

      <CNav variant="tabs" className="mb-3">
        {AGENTS.map((agent) => (
          <CNavItem key={agent.id}>
            <CNavLink active={tab === agent.id} onClick={() => pickTab(agent.id)} style={{ cursor: 'pointer' }}>
              {agent.label}
            </CNavLink>
          </CNavItem>
        ))}
      </CNav>

      <div className="card-x">
        <div className="bia-card-head">
          <h3>
            {current.label}{' '}
            {form.id ? <span className="bia-pill mini">ID {form.id}</span> : <span className="bia-badge warn">nuevo</span>}
            {form.isActive ? <span className="bia-badge ok" style={{ marginLeft: 6 }}>activo</span> : null}
          </h3>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <CButton color="secondary" variant="ghost" disabled={loading || saving} onClick={() => loadPrompts(tab, selectedId)}>
              <CIcon icon={icon('cilReload')} /> Recargar lista
            </CButton>
            <CButton color="secondary" variant="outline" disabled={loading || saving} onClick={resetFromBackend}>
              <CIcon icon={icon('cilReload')} /> Recargar seleccionado
            </CButton>
            <CButton color="secondary" variant="outline" disabled={saving} onClick={saveLocalDraft}>
              <CIcon icon={icon('cilSave')} /> Guardar borrador local
            </CButton>
            {draft ? (
              <CButton color="secondary" variant="ghost" disabled={saving} onClick={loadLocalDraft}>
                <CIcon icon={icon('cilFolderOpen')} /> Cargar borrador local
              </CButton>
            ) : null}
            {draft ? (
              <CButton color="danger" variant="ghost" disabled={saving} onClick={discardLocalDraft}>
                <CIcon icon={icon('cilTrash')} /> Descartar borrador
              </CButton>
            ) : null}
            <CButton color="success" className="btn-brand" disabled={loading || saving} onClick={saveBackend}>
              <CIcon icon={icon('cilCloudUpload')} /> {saving ? 'Guardando...' : 'Guardar'}
            </CButton>
          </div>
        </div>

        <p style={{ margin: '0 0 12px', color: '#768192', fontSize: 12.5 }}>{current.desc}</p>

        <div className="row g-3">
          <div className="col-md-5">
            <CFormLabel>Prompt backend</CFormLabel>
            <CFormSelect value={selectedId} disabled={loading || saving} onChange={(event) => selectPrompt(event.target.value)}>
              <option value="new">Nuevo prompt inactivo</option>
              {prompts.map((item) => (
                <option key={item.id} value={String(item.id)}>
                  #{item.id} - v{item.version} {item.isActive ? '(activo)' : '(inactivo)'} {item.comment ? '- ' + item.comment : ''}
                </option>
              ))}
            </CFormSelect>
          </div>
          <div className="col-md-2">
            <CFormLabel>Version</CFormLabel>
            <CFormInput type="number" min="1" value={form.version} disabled={saving} onChange={(event) => setField('version', event.target.value)} />
          </div>
          <div className="col-md-2">
            <CFormLabel>Activo</CFormLabel>
            <CFormSelect value={String(Boolean(form.isActive))} disabled={saving} onChange={(event) => setField('isActive', event.target.value === 'true')}>
              <option value="false">No</option>
              <option value="true">Si</option>
            </CFormSelect>
          </div>
          <div className="col-md-3">
            <CFormLabel>Usuario</CFormLabel>
            <CFormInput value={form.createdUser || PROMPT_USER} disabled />
          </div>
        </div>

        <div className="mt-3">
          <CFormLabel>Comentario</CFormLabel>
          <CFormInput value={form.comment || ''} disabled={saving} onChange={(event) => setField('comment', event.target.value)} />
        </div>

        <div className="mt-3">
          <CFormLabel>Contenido</CFormLabel>
          <CFormTextarea
            className="bia-mono"
            rows={28}
            style={{ fontSize: 11.5, lineHeight: 1.55 }}
            value={form.content || ''}
            placeholder={loading ? 'Cargando prompts desde /admin/ai...' : ''}
            disabled={saving}
            onChange={(event) => setField('content', event.target.value)}
          />
        </div>

        <small className="text-muted">
          Tamaño: {(form.content || '').length} chars · Fuente: <code>/admin/ai?resource=prompts&amp;action=list</code>
          {form.id ? <> · Guardado: <code>update&amp;id={form.id}</code></> : <> · Guardado: <code>save</code></>}
          {draft ? <> · Borrador local disponible</> : null}
        </small>
      </div>
    </>
  )
}

export default AiPromptView
